/**
 * useAnalytics Hook
 * 
 * React hook for tracking analytics events with automatic user context injection.
 * Provides a simple interface for components to track user behavior.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { 
  analytics, 
  ANALYTICS_EVENTS, 
  getEventCategory,
  type AnalyticsEventProperties,
  type EventCategory,
  type AnalyticsEventName,
} from '@/lib/analytics';

interface UseAnalyticsOptions {
  /** Track page views automatically when route changes */
  trackPageViews?: boolean;
}

export function useAnalytics(options: UseAnalyticsOptions = {}) {
  const { trackPageViews = false } = options;
  const { user } = useAuth();
  const location = useLocation();
  const hasSetContext = useRef(false);

  // Update analytics context when user changes
  useEffect(() => {
    if (user && !hasSetContext.current) {
      analytics.setUserContext({
        userId: user.id,
        organizationId: user.organization_id,
        userRole: user.role as 'client' | 'vendor' | 'admin',
      });
      hasSetContext.current = true;
    } else if (!user && hasSetContext.current) {
      analytics.clearUserContext();
      hasSetContext.current = false;
    }
  }, [user]);

  // Track page views on route change
  useEffect(() => {
    if (trackPageViews) {
      const pageName = getPageNameFromPath(location.pathname);
      analytics.page(pageName, location.pathname);
    }
  }, [location.pathname, trackPageViews]);

  /**
   * Track a custom event
   */
  const track = useCallback((
    eventName: AnalyticsEventName | string,
    properties?: AnalyticsEventProperties,
    category?: EventCategory
  ) => {
    analytics.track({
      name: eventName,
      category: category || getEventCategory(eventName),
      properties: {
        ...properties,
        // Automatically add user context if available
        user_id: user?.id,
        organization_id: user?.organization_id,
        user_role: user?.role as 'client' | 'vendor' | 'admin',
      },
    });
  }, [user?.id, user?.organization_id, user?.role]);

  /**
   * Track a page view
   */
  const page = useCallback((pageName: string, pagePath?: string) => {
    analytics.page(pageName, pagePath);
  }, []);

  /**
   * Identify the current user
   */
  const identify = useCallback((traits?: Record<string, unknown>) => {
    if (user) {
      analytics.identify(user.id, {
        email: user.email,
        role: user.role,
        organization_id: user.organization_id,
        ...traits,
      });
    }
  }, [user]);

  return {
    track,
    page,
    identify,
    events: ANALYTICS_EVENTS,
    sessionId: analytics.getSessionId(),
  };
}

/**
 * Helper to derive a readable page name from the URL path
 */
function getPageNameFromPath(path: string): string {
  if (path === '/') return 'Home';
  
  // Remove leading slash and split by /
  const segments = path.slice(1).split('/');
  
  // Handle common patterns
  const pathPatterns: Record<string, string> = {
    'dashboard': 'Dashboard',
    'jobs': 'Jobs',
    'rfqs': 'RFQs',
    'bids': 'Bids',
    'vendors': 'Vendors',
    'settings': 'Settings',
    'profile': 'Profile',
    'auth': 'Auth',
    'login': 'Login',
    'signup': 'Signup',
    'onboarding': 'Onboarding',
  };

  const firstSegment = segments[0];
  const baseName = pathPatterns[firstSegment] || capitalizeFirst(firstSegment);

  // If there's a second segment, it might be a detail page
  if (segments.length > 1) {
    const secondSegment = segments[1];
    // Check if it looks like an ID (UUID or public ID)
    if (isIdSegment(secondSegment)) {
      return `${baseName} Detail`;
    }
    return `${baseName} - ${pathPatterns[secondSegment] || capitalizeFirst(secondSegment)}`;
  }

  return baseName;
}

function capitalizeFirst(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ');
}

function isIdSegment(segment: string): boolean {
  // UUID pattern or common public ID patterns (like RFQ-XXXX)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const publicIdPattern = /^[A-Z]+-[A-Z0-9]+$/i;
  return uuidPattern.test(segment) || publicIdPattern.test(segment);
}

// Re-export for convenience
export { ANALYTICS_EVENTS } from '@/lib/analytics';
